/**
 * StudentDetailSidebar (11F.7) — the Student detail left rail.
 *
 * Reference: `student1.png` for which blocks exist; D13 for the look.
 *
 * Cross-feature data (spend, attendance %) is injected via `renderInsights`
 * from the route layer — People must not import Billing or Attendance
 * directly (eslint feature-boundary rule), and the same constraint the tabs
 * already work around applies here.
 *
 * Fields in `student1.png` that Candeza's student schema does NOT have —
 * allergies/medical, batches, nationality, structured address, academic
 * details — are deliberately NOT rendered as empty blocks. Showing a
 * permanently blank "Allergies" card would imply the data is missing for
 * this student rather than absent from the product. They are logged as a
 * scope gap in docs/04-modules/students.md instead.
 */

import React from 'react';
import {
  RailCard,
  RailField,
  RailFields,
  RailHeading,
  RailInsight,
  RailInsights,
  RailQuickActions,
  RailSection,
  type QuickAction,
} from '@/components/common';
import type { StudentResponse } from '@features/people/api/students';
import { useGuardian } from '@features/people/api/guardians';
import { formatAge, formatTenure, getFullName } from '@/utils';

/** Digits only — `tel:` and `wa.me` both reject spaces and punctuation. */
function telHref(phone: string | null | undefined, scheme: 'tel' | 'sms'): string | undefined {
  if (!phone) return undefined;
  const cleaned = phone.replace(/[^\d+]/g, '');
  return cleaned ? `${scheme}:${cleaned}` : undefined;
}

function whatsappHref(phone: string | null | undefined): string | undefined {
  if (!phone) return undefined;
  const digits = phone.replace(/\D/g, '');
  return digits ? `https://wa.me/${digits}` : undefined;
}

const GuardianEntry: React.FC<{ guardianId: string }> = ({ guardianId }) => {
  const { data: guardian } = useGuardian(guardianId);
  if (!guardian) return null;
  return (
    <RailField
      label={`${guardian.relationship ?? 'Guardian'}${guardian.is_primary ? ' · primary' : ''}`}
      value={
        <>
          {getFullName(guardian.first_name, guardian.last_name)}
          {guardian.phone && (
            <div style={{ fontWeight: 400, color: '#64748b', fontSize: 13 }}>
              {guardian.phone}
            </div>
          )}
        </>
      }
      testId={`candeza-student-rail-guardian-${guardianId}`}
    />
  );
};

interface Props {
  student: StudentResponse;
  /** Cross-feature insight cells (spend, attendance %) injected by routes. */
  renderInsights?: (studentId: string) => React.ReactNode;
}

export const StudentDetailSidebar: React.FC<Props> = ({ student, renderInsights }) => {
  const age = formatAge(student.date_of_birth);
  const tenure = formatTenure(student.created_at);

  const actions: QuickAction[] = [
    { key: 'call', label: 'Call', href: telHref(student.phone, 'tel') },
    { key: 'message', label: 'Message', href: telHref(student.phone, 'sms') },
    { key: 'whatsapp', label: 'WhatsApp', href: whatsappHref(student.phone) },
  ];

  return (
    <>
      <RailCard>
        <RailQuickActions actions={actions} testId="candeza-student-rail-actions" />
      </RailCard>

      <RailCard data-testid="candeza-student-rail-insights">
        <RailHeading>Insights</RailHeading>
        <RailInsights>
          {renderInsights ? (
            renderInsights(student.id)
          ) : (
            <RailInsight label="Spend" value="—" />
          )}
          <RailInsight
            label="With us"
            value={tenure ?? '—'}
            testId="candeza-student-rail-tenure"
          />
        </RailInsights>
      </RailCard>

      <RailCard>
        <RailHeading>Details</RailHeading>
        <RailFields>
          <RailField
            label="Name"
            value={getFullName(student.first_name, student.last_name)}
          />
          <RailField label="Email" value={student.email} />
          <RailField label="Phone" value={student.phone} />
          <RailField
            label="Birthday"
            value={
              student.date_of_birth
                ? `${new Date(student.date_of_birth).toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}${age ? ` · ${age}` : ''}`
                : null
            }
            testId="candeza-student-rail-birthday"
          />
          <RailField
            label="Gender"
            value={
              student.gender
                ? student.gender.charAt(0).toUpperCase() + student.gender.slice(1)
                : null
            }
          />
          <RailField
            label="Student ID"
            value={student.classcard_id ?? student.id}
            testId="candeza-student-rail-student-id"
          />
          <RailField label="Branch" value={student.branch_id} />
        </RailFields>
      </RailCard>

      {(student.guardian_ids ?? []).length > 0 && (
        <RailSection
          title="Family"
          defaultOpen
          testId="candeza-student-rail-family"
        >
          <RailFields>
            {(student.guardian_ids ?? []).map((gid) => (
              <GuardianEntry key={gid} guardianId={gid} />
            ))}
          </RailFields>
        </RailSection>
      )}

      {((student.tags ?? []).length > 0 || student.notes) && (
        <RailSection title="Miscellaneous" testId="candeza-student-rail-misc">
          <RailFields>
            {(student.tags ?? []).length > 0 && (
              <RailField label="Tags" value={(student.tags ?? []).join(', ')} />
            )}
            {student.notes && <RailField label="Notes" value={student.notes} />}
          </RailFields>
        </RailSection>
      )}
    </>
  );
};

export default StudentDetailSidebar;
